import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface EducationLevelData {
  level_name: string;
  level_id: number;
  schools: {
    total_schools: number;
    government_schools: number;
    private_schools: number;
  };
  enrollment: {
    total_enrolled: number;
    female_enrolled: number;
    male_enrolled: number;
    new_entrants: number;
    average_age: number;
    gender_parity_index: number;
  };
  grade_distribution: Array<{
    grade_code: string;
    total_students: number;
    female_students: number;
    male_students: number;
    gender_parity_index: number;
  }>;
  completion_and_dropout: {
    previous_year_enrollment: number;
    current_year_enrollment: number;
    dropout_rate: number;
    retention_rate: number;
    completion_rate: number;
    analysis_method: string;
  };
  geographic_breakdown: Array<{
    district_name: string;
    region_name: string;
    student_count: number;
    school_count: number;
  }>;
}

interface ReportData {
  academic_year: number;
  report_date: string;
  filters: {
    district?: string;
    region?: string;
  };
  education_levels: Record<string, EducationLevelData>;
  summary: {
    total_schools: number;
    total_students: number;
    total_females: number;
    total_males: number;
    education_levels_offered: number;
    overall_gender_parity_index: number;
    female_percentage: number;
    male_percentage: number;
  };
}

const EducationLevelReports: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');

  const { data: reportData, isLoading, error, refetch } = useQuery<{ data: ReportData }>({
    queryKey: ['education-level-reports', selectedYear, selectedDistrict, selectedRegion],
    queryFn: async () => {
      const token = localStorage.getItem('emis_auth_token');
      const params = new URLSearchParams();
      if (selectedYear) params.append('year', selectedYear);
      if (selectedDistrict) params.append('district', selectedDistrict);
      if (selectedRegion) params.append('region', selectedRegion);
      
      const response = await axios.get(
        `${API_URL}/sis-integration/education-level-reports/?${params.toString()}`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      return response.data;
    },
  });

  const getRiskColor = (rate: number) => {
    if (rate > 15) return 'error';
    if (rate > 10) return 'warning';
    return 'success';
  };

  const getGenderParityColor = (index: number) => {
    if (index >= 0.9 && index <= 1.1) return 'success';
    if (index >= 0.8 && index <= 1.2) return 'warning';
    return 'error';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !reportData) {
    return (
      <Alert severity="error">
        Failed to load education level reports. Please try again.
      </Alert>
    );
  }

  const report = reportData.data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Education Level Reports
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            color="primary"
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Academic Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <MenuItem value="">Current Year ({report.academic_year})</MenuItem>
                  <MenuItem value={String(report.academic_year - 1)}>
                    {report.academic_year - 1}
                  </MenuItem>
                  <MenuItem value={String(report.academic_year - 2)}>
                    {report.academic_year - 2}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegion}
                  label="Region"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  <MenuItem value="Unguja">Unguja</MenuItem>
                  <MenuItem value="Pemba">Pemba</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>District</InputLabel>
                <Select
                  value={selectedDistrict}
                  label="District"
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {/* Add district options based on available data */}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SchoolIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Schools
                </Typography>
              </Box>
              <Typography variant="h4">
                {report.summary.total_schools.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Students
                </Typography>
              </Box>
              <Typography variant="h4">
                {report.summary.total_students.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Gender Distribution
              </Typography>
              <Typography variant="body2">
                Female: {report.summary.female_percentage}%
              </Typography>
              <Typography variant="body2">
                Male: {report.summary.male_percentage}%
              </Typography>
              <Chip
                label={`GPI: ${report.summary.overall_gender_parity_index}`}
                size="small"
                color={getGenderParityColor(report.summary.overall_gender_parity_index)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Education Levels
                </Typography>
              </Box>
              <Typography variant="h4">
                {report.summary.education_levels_offered}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Education Level Details */}
      {Object.entries(report.education_levels).map(([levelName, levelData]) => (
        <Accordion key={levelName} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {levelName}
              </Typography>
              <Chip
                label={`${levelData.enrollment.total_enrolled.toLocaleString()} students`}
                color="primary"
                variant="outlined"
                sx={{ mr: 2 }}
              />
              <Chip
                label={`${levelData.schools.total_schools} schools`}
                color="secondary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Schools Information */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Schools Overview
                    </Typography>
                    <Typography variant="body2">
                      Total Schools: {levelData.schools.total_schools}
                    </Typography>
                    <Typography variant="body2">
                      Government: {levelData.schools.government_schools}
                    </Typography>
                    <Typography variant="body2">
                      Private: {levelData.schools.private_schools}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Enrollment Information */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Enrollment Statistics
                    </Typography>
                    <Typography variant="body2">
                      Total Enrolled: {levelData.enrollment.total_enrolled.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Female: {levelData.enrollment.female_enrolled.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Male: {levelData.enrollment.male_enrolled.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      New Entrants: {levelData.enrollment.new_entrants.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Average Age: {levelData.enrollment.average_age} years
                    </Typography>
                    <Chip
                      label={`GPI: ${levelData.enrollment.gender_parity_index}`}
                      size="small"
                      color={getGenderParityColor(levelData.enrollment.gender_parity_index)}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Completion & Dropout */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Completion & Dropout Analysis
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Dropout Rate: {levelData.completion_and_dropout.dropout_rate}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={levelData.completion_and_dropout.dropout_rate}
                        color={getRiskColor(levelData.completion_and_dropout.dropout_rate)}
                        sx={{ mb: 1 }}
                      />
                    </Box>
                    <Typography variant="body2">
                      Retention Rate: {levelData.completion_and_dropout.retention_rate}%
                    </Typography>
                    <Typography variant="body2">
                      Completion Rate: {levelData.completion_and_dropout.completion_rate}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Method: {levelData.completion_and_dropout.analysis_method}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Grade Distribution */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Grade Distribution
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Grade</TableCell>
                            <TableCell align="right">Total Students</TableCell>
                            <TableCell align="right">Female</TableCell>
                            <TableCell align="right">Male</TableCell>
                            <TableCell align="center">Gender Parity Index</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {levelData.grade_distribution.map((grade) => (
                            <TableRow key={grade.grade_code}>
                              <TableCell>{grade.grade_code}</TableCell>
                              <TableCell align="right">
                                {grade.total_students.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">
                                {grade.female_students.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">
                                {grade.male_students.toLocaleString()}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={grade.gender_parity_index}
                                  size="small"
                                  color={getGenderParityColor(grade.gender_parity_index)}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Geographic Breakdown */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Geographic Distribution
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>District</TableCell>
                            <TableCell>Region</TableCell>
                            <TableCell align="right">Students</TableCell>
                            <TableCell align="right">Schools</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {levelData.geographic_breakdown.map((geo, index) => (
                            <TableRow key={index}>
                              <TableCell>{geo.district_name}</TableCell>
                              <TableCell>{geo.region_name}</TableCell>
                              <TableCell align="right">
                                {geo.student_count.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">
                                {geo.school_count}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Report Metadata */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Information
          </Typography>
          <Typography variant="body2">
            Academic Year: {report.academic_year}
          </Typography>
          <Typography variant="body2">
            Report Generated: {new Date(report.report_date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2">
            Data Source: Real SIS Database
          </Typography>
          {report.filters.district && (
            <Typography variant="body2">
              District Filter: {report.filters.district}
            </Typography>
          )}
          {report.filters.region && (
            <Typography variant="body2">
              Region Filter: {report.filters.region}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EducationLevelReports;